import PropTypes from "prop-types";
import * as React from "react";
import { Box, Button, TextInput, Typography } from "../../themeV2";

const CODE_FIELDS = Object.freeze({
  password: {
    label: "Password",
    description: undefined,
    type: "password",
    autoComplete: "current-password",
  },
  totp: {
    label: "Authentication code",
    description: "Enter the 6-digit code from your authenticator app.",
    inputMode: "numeric",
    autoComplete: "one-time-code",
  },
  recovery: {
    label: "Recovery code",
    description: "Enter one of the recovery codes you saved. Each works once.",
    inputMode: "text",
    autoComplete: "off",
  },
});

const SWITCH_LABEL = Object.freeze({
  password: "Use your password",
  totp: "Use your authenticator app",
  passkey: "Use a passkey",
  recovery: "Use a recovery code",
});

const CodeForm = ({ method, onCode, isSubmitting }) => {
  const [code, setCode] = React.useState("");
  const [problem, setProblem] = React.useState(null);
  const field = CODE_FIELDS[method];

  const handleSubmit = (event) => {
    event.preventDefault();

    return code.trim()
      ? onCode({ method, code: code.trim() })
      : setProblem(`Please enter your ${field.label.toLowerCase()}`);
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <Box direction="column" gap={4}>
        <TextInput
          type={field.type}
          label={field.label}
          description={field.description}
          variant="filled"
          inputMode={field.inputMode}
          autoComplete={field.autoComplete}
          autoCapitalize="off"
          spellCheck={false}
          autoFocus
          required
          value={code}
          onValueChange={setCode}
          error={problem ?? undefined}
        />
        <Button type="submit" fullWidth loading={isSubmitting}>
          Verify
        </Button>
      </Box>
    </form>
  );
};

CodeForm.propTypes = {
  method: PropTypes.oneOf(["totp", "recovery", "password"]).isRequired,
  onCode: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
};

const PasskeyPrompt = ({ onPasskey, isSubmitting }) => (
  <Box direction="column" gap={4}>
    <Typography variant="body-md" color="variant">
      Use the passkey saved on this device, your phone, or your security key.
    </Typography>
    <Button fullWidth onClick={onPasskey} loading={isSubmitting}>
      Use your passkey
    </Button>
  </Box>
);

PasskeyPrompt.propTypes = {
  onPasskey: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
};

export default function SecondFactorStep({
  methods,
  method,
  onChoose,
  onCode,
  onPasskey,
  isSubmitting,
  showHeading,
}) {
  const others = methods.filter((other) => other !== method);

  return (
    <Box direction="column" gap={4}>
      {showHeading && (
        <Typography variant="title-md" as="h2">
          Confirm it&apos;s you
        </Typography>
      )}

      {method === "passkey" ? (
        <PasskeyPrompt
          key={method}
          onPasskey={onPasskey}
          isSubmitting={isSubmitting}
        />
      ) : (
        <CodeForm
          key={method}
          method={method}
          onCode={onCode}
          isSubmitting={isSubmitting}
        />
      )}

      {others.length > 0 && (
        <Box direction="column" align="start" gap={1}>
          {others.map((other) => (
            <Button
              key={other}
              variant="text"
              size="sm"
              onClick={() => onChoose(other)}
            >
              {SWITCH_LABEL[other]}
            </Button>
          ))}
        </Box>
      )}
    </Box>
  );
}

const METHOD = PropTypes.oneOf(["totp", "passkey", "recovery", "password"]);

SecondFactorStep.propTypes = {
  methods: PropTypes.arrayOf(METHOD).isRequired,
  method: METHOD.isRequired,
  onChoose: PropTypes.func.isRequired,
  onCode: PropTypes.func.isRequired,
  onPasskey: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  showHeading: PropTypes.bool,
};

SecondFactorStep.defaultProps = {
  showHeading: true,
};
